/**
 * The module cards at the top of Online Training (2026-11-02): one card per
 * course — a service (Mass Notification, Audio Conferencing, …) or an
 * extension — grouped under those two headings. A card shows how many
 * videos the course has, how long it runs, and how far this viewer got;
 * it opens the player where they left off.
 */
import { Box, Chip, LinearProgress, Typography } from "@mui/material";
import {
  BellRing,
  Disc3,
  GraduationCap,
  Headphones,
  Mic,
  Puzzle,
  Radio,
  Video,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { ModuleCategory, ModuleSummary } from "./modules-api.js";
import { modulePath, moduleMeta, progressOf, readWatched } from "./module-player.js";

const GROUPS: Array<{ category: ModuleCategory; title: string }> = [
  { category: "service", title: "Services" },
  { category: "extension", title: "Extensions" },
];

export function ModuleCards({ modules }: { modules: ModuleSummary[] }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid="training-modules">
      {GROUPS.map(({ category, title }) => {
        const list = modules.filter((m) => m.category === category);
        if (list.length === 0) return null;
        return (
          <Box component="section" key={category} aria-label={title} sx={{ display: "grid", gap: 1.5 }} data-testid={`module-group-${category}`}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700 }}>
              {title}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: "repeat(3, minmax(0,1fr))", xl: "repeat(4, minmax(0,1fr))" },
              }}
            >
              {list.map((m) => (
                <ModuleCard key={m.id} mod={m} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/** A recognisable picture for a course, from its title. */
export function moduleIcon(mod: Pick<ModuleSummary, "title" | "category">): LucideIcon {
  const t = mod.title.toLowerCase();
  if (/notif|alert|broadcast/.test(t)) return BellRing;
  if (/conferenc|bridge|meeting/.test(t)) return Headphones;
  if (/record/.test(t)) return Disc3;
  if (/ivr|menu|flow/.test(t)) return Workflow;
  if (/voice|audio/.test(t)) return Mic;
  if (/video|webcast|stream/.test(t)) return Video;
  if (/dispatch|radio|ptt|gateway/.test(t)) return Radio;
  return mod.category === "extension" ? Puzzle : GraduationCap;
}

function ModuleCard({ mod }: { mod: ModuleSummary }): JSX.Element {
  const watched = readWatched(mod.slug).size;
  const progress = progressOf(readWatched(mod.slug), mod.topic_count);
  const Icon = moduleIcon(mod);
  const started = watched > 0;
  const finished = mod.topic_count > 0 && progress >= 1;

  return (
    <Box
      component={RouterLink}
      to={modulePath(mod.slug)}
      data-testid={`module-card-${mod.slug}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        color: "text.primary",
        textDecoration: "none",
        transition: "transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, border-color 180ms",
        "&:hover, &:focus-visible": {
          transform: "translateY(-2px)",
          borderColor: "primary.main",
          boxShadow: "0 10px 28px rgba(15,23,42,.12)",
        },
        "&:hover .module-play, &:focus-visible .module-play": { opacity: 1, transform: "scale(1)" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16 / 7",
          display: "grid",
          placeItems: "center",
          color: "common.white",
          // The same deep navy as the player's stage, in both themes: the
          // palette's primary is a pale periwinkle in dark mode.
          background: "radial-gradient(120% 140% at 0% 0%, #2a4f8f 0%, #13285a 50%, #0a1633 100%)",
        }}
      >
        <Icon size={44} strokeWidth={1.6} />
        <Chip
          size="small"
          label={mod.category === "extension" ? "Extension" : "Service"}
          sx={{ position: "absolute", top: 10, left: 10, height: 22, fontWeight: 600, bgcolor: "rgba(255,255,255,.18)", color: "common.white" }}
        />
        <Box
          className="module-play"
          sx={{
            position: "absolute",
            right: 12,
            bottom: 12,
            px: 1.25,
            py: 0.5,
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: "rgba(255,255,255,.95)",
            color: "primary.main",
            opacity: 0,
            transform: "scale(.94)",
            transition: "opacity 160ms, transform 160ms",
          }}
        >
          {finished ? "Watch again" : started ? "Continue" : "Start"}
        </Box>
      </Box>
      <Box sx={{ p: 2, display: "grid", gap: 0.75, flex: 1, alignContent: "start" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{mod.title}</Typography>
        {mod.summary && (
          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {mod.summary}
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, color: "text.disabled", mt: "auto" }}>{moduleMeta(mod.topic_count, mod.total_duration_seconds)}</Typography>
        {started && (
          <LinearProgress
            variant="determinate"
            value={progress * 100}
            color={finished ? "success" : "primary"}
            aria-label={`${mod.title} progress`}
            sx={{ height: 4, borderRadius: 2, bgcolor: "surfaceSoft", "& .MuiLinearProgress-bar": { borderRadius: 2 } }}
          />
        )}
      </Box>
    </Box>
  );
}
